import React, { useContext, useLayoutEffect, useRef } from 'react'
import type { FormStepProps } from '@redventures/odyssey'
import { OdysseyContext } from '@redventures/odyssey'
import { ProgressBar } from '../components/ProgressBar'
import { QuestionnaireHeader } from '../components/QuestionnaireHeader'
import { QuestionnaireFooter } from '../components/QuestionnaireFooter'
import { Tooltip } from '../components/Tooltip'
import QuestionContainerRoot from '../components/QuestionContainerRoot'
import { resolveTrackingValues } from '../../utils/resolveTrackingValues'
import { useStoreActions, useStoreState } from '../../store/useStore'
import useElementViewed from '@strata-monorepo/tracking/src/hooks/useElementViewed'
import { CustomDimensionsKeys, ElementType } from '@strata-monorepo/tracking/src/types'
import { type FmpFormStep } from '@strata-monorepo/find-my-plan/types/form'
import { useQuestionnaireActions } from '../QuestionnaireActionsContext'
import { QuestionStepBody } from './QuestionStepBody'
import { useQuestionnaireFooterSkip } from './useQuestionnaireFooterSkip'
import type { FormStepRef } from './formStepRef'
import { useMonarchRule } from '@redventures/cohesion-utils-react'

export type { FormStepRef }

interface QuestionProps {
  title: string
  label: string
  subtitle?: string
  tooltip?: string
  canSkip?: boolean
  formValue?: string
  tracking?: {
    stepName?: string
    stepNumber?: number
    stepId?: string
    deepStepNumber?: number
    quickStepNumber?: number
    deepStepId?: string
    quickStepId?: string
    fieldName?: string
  }
}

type PropsWithResolvedTracking = FormStepProps

const QuestionWrapper: React.FC<FormStepProps> = (props) => {
  const { goNext, goBack, formStep } = props
  const formStepRef = useRef<FormStepRef>(null)
  const { onSkipToResults } = useQuestionnaireActions()
  const odysseyContext = useContext(OdysseyContext)

  const isCurrentStepValid = odysseyContext?.getValidationResultForCurrentStep() ?? true

  const { componentProps } = formStep
  const { title, subtitle, label, tooltip, canSkip, formValue, tracking } =
    componentProps as QuestionProps

  const isDeepDive = useStoreState((state) => state.form.isDeepDiveFlow)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)
  const initialStepIndex = useStoreState((state) => state.form.initialStepIndex)
  const filteredQuestions = useStoreState((state) => state.form.filteredQuestions)
  const formResults = useStoreState((state) => state.form.formResults)
  const progressDisplay = useStoreState((state) => state.form.progressDisplay)
  const currentProgress = useStoreState((state) => state.form.checkpointProgress)
  const currentStepNumber = useStoreState((state) => state.form.currentStepNumber)
  const totalSteps = useStoreState((state) => state.form.totalSteps)
  const shouldShowSteps = progressDisplay.showSteps

  const setCurrentStep = useStoreActions((actions) => actions.form.setCurrentStep)
  const restorePreviousProgress = useStoreActions((actions) => actions.form.restorePreviousProgress)

  const propsWithTrackingSteps: PropsWithResolvedTracking = {
    ...props,
    formStep: {
      ...formStep,
      componentProps: {
        ...componentProps,
        tracking: resolveTrackingValues(tracking, isDeepDive),
      },
    },
  }

  const formStepTyped = formStep as FmpFormStep

  const { skipQuestionLabel, effectiveCanSkip, footerSkipCompletesQuiz } =
    useQuestionnaireFooterSkip({
      formStep: formStepTyped,
      canSkip,
      filteredQuestions,
      currentStepIndex,
      currentStepNumber,
      totalSteps,
      progressDisplay,
      formResults,
    })

  const { value: monarchFinalCtaLabel, isLoading: isFinalCtaLabelLoading } =
    useMonarchRule<string>('fmpFinalCtaLabel')
  // Defer to 'Show Results' while Monarch resolves (prevents label flicker) and
  // guard against misconfigured rules returning '' or a non-string value.
  const validMonarchLabel =
    !isFinalCtaLabelLoading && typeof monarchFinalCtaLabel === 'string' && monarchFinalCtaLabel
  const finalCtaLabel = footerSkipCompletesQuiz ? validMonarchLabel || 'Show Results' : undefined

  const handleGoNext = async (options?: { skipValidation?: boolean }) => {
    if (formStepRef.current?.onBeforeNext) {
      const canProceed = await formStepRef.current.onBeforeNext()
      if (!canProceed) {
        return
      }
    }

    goNext(options)
  }

  const handleBack = () => {
    restorePreviousProgress()
    goBack()
  }

  useLayoutEffect(() => {
    setCurrentStep(formStep)
  }, [formStep, setCurrentStep])

  const progressBarRef = useElementViewed({
    elementViewedArgs: {
      webElement: {
        elementType: ElementType.Section,
        location: 'quiz',
        name: 'progress-bar',
        position: '',
        text: 'progress bar',
      },
      customDimensions: [
        {
          key: CustomDimensionsKeys.PROGRESS_BAR,
          value: `${currentProgress}%`,
        },
      ],
    },
    opts: { triggerOnce: true },
  })

  const shouldShowTitle = formStep.name !== 'uploadBill'
  const shouldShowProgressBar =
    formStep.name !== 'interstitialRadioGroup' && progressDisplay.showProgressBar !== false

  return (
    <div className='strata:@container strata:flex strata:flex-col strata:min-h-dvh'>
      <QuestionnaireHeader
        onSkipToResults={onSkipToResults}
        isLastQuestion={footerSkipCompletesQuiz}
      />
      <QuestionContainerRoot hasProgressBar={shouldShowProgressBar}>
        <div ref={progressBarRef}>
          {shouldShowProgressBar && (
            <ProgressBar
              value={currentProgress}
              label={label}
              currentStepNumber={currentStepNumber}
              totalSteps={totalSteps}
              showSteps={shouldShowSteps}
            />
          )}
        </div>

        {shouldShowTitle && (
          <div className='strata:text-center'>
            <h2
              className='strata:text-title-base strata:@qm/qc:text-title-xl strata:font-bold strata:lg:whitespace-nowrap strata:lg:w-max strata:lg:max-w-[100vw] strata:lg:overflow-hidden strata:lg:text-ellipsis strata:lg:relative strata:lg:left-1/2 strata:lg:-translate-x-1/2'
              dangerouslySetInnerHTML={{ __html: title }}
            />

            {subtitle && (
              <p className='strata:text-body-sm strata:@qm/qc:text-body-base strata:mt-2 strata:@qm/qc:mt-4'>
                {subtitle}
              </p>
            )}
          </div>
        )}

        {tooltip && <Tooltip content={tooltip} />}

        <div className='strata:text-center strata:flex strata:flex-col strata:gap-4'>
          <QuestionStepBody
            stepName={formStep.name}
            propsWithTrackingSteps={propsWithTrackingSteps}
            formStepRef={formStepRef}
          />
        </div>
      </QuestionContainerRoot>
      <QuestionnaireFooter
        goBack={handleBack}
        restrictedBack={formValue === 'interstitial' || currentStepIndex <= initialStepIndex}
        skipQuestionLabel={footerSkipCompletesQuiz ? undefined : skipQuestionLabel}
        canSkip={footerSkipCompletesQuiz ? false : effectiveCanSkip}
        goNext={handleGoNext}
        disabled={!isCurrentStepValid}
        nextLabel={finalCtaLabel}
      />
    </div>
  )
}

export default QuestionWrapper
